import { ModalScreens } from 'components/Navigation/types';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL, eventSelectScreenSelection } from 'store/actions';
import {
  selectRemoteCustomers,
  selectRemoteOrganisation,
} from 'store/remote/selectors';
import { useSelector } from 'hooks/useSelector';
import { UID } from 'types/types';
import { cloneDeep, isEqual, sortBy } from 'lodash';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { theme } from 'lib/theme';
import { ICONS } from 'components/Icon';
import { randomKey, safeAreaBottomIsRequired } from 'lib/riskbase';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { View } from 'react-native';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { AssessmentTemplate } from 'store/remote/types';
import { ScrollView } from 'components/ScrollView';
import { TemplatePricingItem } from './ListItems/TemplatePricingItem';

type Props = {
  costs: AssessmentTemplate['costs'];
  defaultCost: number;
  defaultMinCost: number;
  defaultMaxCost: number | null;
  onChange: (costs: AssessmentTemplate['costs']) => void;
  onClose: () => void;
};

export const CostsScreen: React.FC<Props> = ({
  costs: incomingCosts,
  defaultCost,
  defaultMinCost,
  defaultMaxCost,
  onChange,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { bottom } = useSafeAreaInsets();
  const forceUpdate = useForceUpdate();

  const eventKeySelectOrganisations = React.useRef(randomKey());

  const organisation = useSelector(selectRemoteOrganisation)!;
  const customers = useSelector(selectRemoteCustomers)!;

  const allOrganisations = [
    organisation,
    ...sortBy(Object.values(customers), 'name'),
  ];

  const costs = cloneDeep(incomingCosts);
  const dataRef = React.useRef(cloneDeep(costs));
  const hasNonDefault = Object.keys(dataRef.current).length > 1;

  const nonCustomisedOrganisations = sortBy(
    Object.values(customers).reduce<(typeof customers)[string][]>(
      (acc, customer) => {
        if (!dataRef.current[customer.id]) {
          acc.push(customer);
        }
        return acc;
      },
      [],
    ),
    'name',
  );

  const checkIsDirty = () => !isEqual(dataRef.current, costs);
  const isDirty = checkIsDirty();

  const handleChangeCost = (
    orgId: UID,
    newCosts: NonNullable<typeof costs>[string],
  ) => {
    dataRef.current = {
      ...dataRef.current,
      [orgId]: newCosts,
    };
    onChange(dataRef.current);
    if (checkIsDirty() !== isDirty) {
      forceUpdate();
    }
  };

  const handleDeleteCost = (orgId: UID) => {
    dataRef.current[orgId] = null;
    onChange(dataRef.current);
    forceUpdate();
  };

  React.useEffect(() => {
    const events = [
      doAddEvent(eventSelectScreenSelection, (event) => {
        if (event.payload.key === eventKeySelectOrganisations.current) {
          dataRef.current = event.payload.stringValues.reduce(
            (acc, organisationId) => {
              acc[organisationId] = {
                cost: null,
                maxCost: null,
                minCost: null,
                fieldGroups: {},
              };
              return acc;
            },
            dataRef.current,
          );
          onChange(dataRef.current);
          forceUpdate();
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [forceUpdate, onChange]);

  return (
    <>
      <ScrollView
        contentPaddingTop={theme.paddingPageVertical}
        contentPaddingBottom={theme.paddingPageVertical}
        keyboardAvoiding
      >
        <Card
          explanation={
            '*Min Price* Combines the document and element prices ' +
            '(e.g. £5 Base, £1 Per Fire Door and £10 Minimum: 0 to 5 Doors = £10, 6 Doors = £11, 7 Doors = £12). ' +
            '*Max Price* Limits the overall cost and is applied last so may override a minimum price'
          }
        />
        <ViewWithSeparators separatorMargin={theme.paddingVertical}>
          {allOrganisations.reduce<React.ReactElement[]>((acc, org) => {
            const isDefault = org.id === organisation.id;
            if (isDefault || dataRef.current[org.id]) {
              acc.push(
                <TemplatePricingItem
                  key={org.id}
                  id={org.id}
                  isDefault={isDefault}
                  initIsExpanded={isDefault && !hasNonDefault}
                  name={
                    isDefault
                      ? `Default Pricing for ${org.name} and Customers`
                      : org.name
                  }
                  defaultCost={defaultCost}
                  defaultMinCost={defaultMinCost}
                  defaultMaxCost={defaultMaxCost}
                  costs={dataRef.current[org.id]}
                  onChange={handleChangeCost}
                  onDelete={handleDeleteCost}
                  onClose={onClose}
                />,
              );
            }
            return acc;
          }, [])}
        </ViewWithSeparators>
      </ScrollView>
      {!!nonCustomisedOrganisations.length && (
        <View
          style={{
            paddingTop: theme.paddingHorizontal,
            paddingBottom:
              theme.paddingHorizontal +
              (safeAreaBottomIsRequired(true) ? bottom : 0),
            borderTopWidth: 1,
            borderTopColor: '#dddddd',
          }}
        >
          <Card margin={false}>
            <CardRow
              label="Add Customisation"
              icon={ICONS.PlusLight}
              labelColor={theme.colors.blue}
              iconColor={theme.colors.blue}
              onPress={() =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.SELECT,
                    params: {
                      title: 'Organisations',
                      searchable: true,
                      multiSelect: true,
                      eventKey: eventKeySelectOrganisations.current,
                      options: nonCustomisedOrganisations.map((customer) => ({
                        name: customer.name,
                        internalValue: customer.id,
                      })),
                    },
                  }),
                )
              }
            />
          </Card>
        </View>
      )}
    </>
  );
};
